(() => {
  'use strict';

  // Theme sections can be reloaded by Shopify's editor without a page reload.
  if (window.FFHStorefront) {
    window.FFHStorefront.mount(document);
    return;
  }

  const instances = new WeakMap();
  const monthlyTerms = 'Renews automatically each month. To stop the next payment and order, cancel at least 48 hours before your next payment. Orders already charged can be cancelled before dispatch for a refund of the product and delivery.';
  const preorderTerms = 'Pre-order: dispatch in approximately 2 weeks. Full payment is taken upfront. Cancel before dispatch for a refund of the product and delivery.';

  function createStorefront(root) {
    const lifecycle = new AbortController();
    const requests = new Set();
    const $ = selector => root.querySelector(selector);
    const $$ = selector => [...root.querySelectorAll(selector)];
    const on = (target, type, handler) => target?.addEventListener(type, handler, { signal: lifecycle.signal });
    const setText = (selector, value) => { const node = $(selector); if (node) node.textContent = value; };
    const form = $('#ffh-product-form');
    const submit = $('#ffh-add-to-cart');
    const quantity = $('#ffh-quantity');
    const variantInput = $('#ffh-variant-id') || form?.querySelector('input[name="id"]');
    const planInput = form?.querySelector('input[name="selling_plan"]');
    let data;
    try {
      const source = $('[data-ffh-product]');
      data = JSON.parse(source?.textContent || root.dataset.ffhProduct || '{}');
    } catch (_) {
      // Keep the native Shopify product form usable if section data is invalid.
      data = {};
    }

    function route(attribute, suffix) {
      const base = window.Shopify?.routes?.root || '/';
      const value = root.dataset[attribute] || `${base.replace(/\/?$/, '/')}${suffix}`;
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid storefront route.');
      return url.pathname + url.search;
    }

    let cartURL;
    let cartJSONURL;
    let addURL;
    let formatter;
    try {
      cartURL = route('cartUrl', 'cart');
      cartJSONURL = route('cartJsonUrl', 'cart.js');
      addURL = route('cartAddUrl', 'cart/add.js');
      formatter = new Intl.NumberFormat(data.locale || document.documentElement.lang || 'en-AU', {
        style: 'currency', currency: data.currency || window.Shopify?.currency?.active || 'AUD'
      });
    } catch (_) {
      return { destroy: () => lifecycle.abort() };
    }

    const money = cents => Number.isFinite(cents) ? formatter.format(cents / 100) : 'Unavailable';
    let variants = Array.isArray(data.variants) ? data.variants : [];
    const originalVariantPacks = variants.map(variant => `${variant.id}:${variant.pack}`).sort().join('|');
    const variantFor = pack => variants.find(variant => String(variant.pack) === String(pack));
    let preferredMonthlyPlanID = null;
    const monthlyPlan = (variant, preferredID = preferredMonthlyPlanID) => {
      const plans = variant?.sellingPlanAllocations?.filter(allocation =>
        allocation.monthly === true && allocation.id != null && Number.isSafeInteger(allocation.price) && allocation.price >= 0) || [];
      return plans.find(allocation => String(allocation.id) === preferredID) || plans[0];
    };
    const selectedPack = () => $('[name="pack"]:checked')?.value;
    const selectedVariant = () => variantFor(selectedPack()) || variants.find(variant => String(variant.id) === variantInput?.value);
    const selectedMode = () => $('[name="purchase"]:checked')?.value || 'once';
    const selectedQuantity = () => {
      const value = Number(quantity?.value);
      return Number.isSafeInteger(value) && value >= 1 ? value : null;
    };

    let cart = null;
    let cartPending = false;
    let productPending = false;
    let productNeedsRetry = false;
    let busy = false;
    let unknownAddOutcome = false;
    let galleryView = 'pack';
    const cartLink = $('#ffh-cart-link');
    if (cartLink) cartLink.href = cartURL;
    const status = message => setText('#ffh-cart-status', message);
    const error = message => {
      const node = $('#ffh-cart-error');
      if (node) { node.textContent = message; node.hidden = !message; }
    };
    const inCart = variant => (cart?.items || []).reduce((count, item) =>
      String(item.variant_id ?? item.id) === String(variant?.id) ? count + (Number(item.quantity) || 0) : count, 0);

    function availability(variant, requested) {
      if (productPending) return { canAdd: false, message: 'Checking current stock and prices…', preorder: false };
      if (productNeedsRetry) return { canAdd: false, message: 'Current stock and prices could not be verified. Retry the availability check below.', preorder: false };
      if (!variant || variant.available !== true) return { canAdd: false, message: 'Sold out', preorder: false };
      if (!requested) return { canAdd: false, message: 'Enter a whole number of packs, at least 1.', preorder: false };
      if (!cart) return { canAdd: false, message: cartPending ? 'Checking availability…' : 'Availability could not be checked. Try again below.', preorder: false };
      const tracked = Boolean(variant.inventoryManagement) && Number.isFinite(variant.inventoryQuantity);
      const stock = tracked ? Math.max(0, variant.inventoryQuantity) : null;
      const remaining = tracked ? Math.max(0, stock - inCart(variant)) : null;
      const exceedsStock = tracked && requested > remaining;
      const preorder = exceedsStock && variant.inventoryPolicy === 'continue';
      if (exceedsStock && !preorder) {
        return { canAdd: false, preorder: false, remaining, message: remaining > 0
          ? `Only ${remaining} more ${remaining === 1 ? 'pack is' : 'packs are'} available with the quantity already in your cart.`
          : 'The available stock for this pack is already in your cart.' };
      }
      return { canAdd: true, preorder, remaining, message: preorder ? preorderTerms : tracked ? 'In stock' : 'Available to order' };
    }

    function syncSelection() {
      const variant = selectedVariant();
      const monthly = $('[name="purchase"][value="monthly"]');
      const once = $('[name="purchase"][value="once"]');
      const plan = monthlyPlan(variant);
      if (monthly) monthly.disabled = busy || !plan;
      if (once) once.disabled = busy || variant?.requiresSellingPlan === true;
      if (selectedMode() === 'monthly' && !plan && once && variant?.requiresSellingPlan !== true) {
        once.checked = true;
        status('Monthly subscriptions are not available for this pack. One-time purchase is selected.');
      } else if (variant?.requiresSellingPlan === true && selectedMode() !== 'monthly' && monthly && plan) {
        monthly.checked = true;
      }
      const isMonthly = selectedMode() === 'monthly';
      const selectedPlan = isMonthly ? plan : null;
      const amount = selectedQuantity();
      const price = isMonthly ? selectedPlan?.price : variant?.price;
      const state = availability(variant, amount);
      const planValid = isMonthly ? Boolean(selectedPlan) : variant?.requiresSellingPlan !== true;
      if (variantInput) variantInput.value = variant?.id ?? '';
      if (planInput) {
        planInput.value = selectedPlan?.id ?? '';
        planInput.disabled = !selectedPlan;
      }
      for (const radio of $$('[name="pack"]')) {
        radio.disabled = busy;
        const item = variantFor(radio.value);
        const itemPrice = isMonthly ? monthlyPlan(item)?.price : item?.price;
        setText(`#price-${radio.value}`, money(itemPrice));
        setText(`#unit-${radio.value}`, Number.isFinite(itemPrice) ? `${money(itemPrice / Number(radio.value))} per shot` : 'Monthly plan unavailable');
      }
      setText('#purchase-description', isMonthly ? monthlyTerms : 'A single order, whenever you feel like it.');
      setText('#summary-item', `${amount || 1} × ${variant?.pack || selectedPack() || ''}-shot pack · ${isMonthly ? 'monthly' : 'one-time'}`);
      setText('#summary-price', money(price));
      setText('#summary-total', amount && Number.isFinite(price) ? money(price * amount) : '—');
      setText('#total-label', isMonthly ? 'Monthly subtotal' : 'Subtotal');
      setText('#recurring-note', isMonthly
        ? 'Renews monthly. Delivery is charged on each order and confirmed at checkout.'
        : 'Delivery is calculated at checkout.');
      setText('#ffh-availability', !planValid ? 'This purchase option is unavailable for the selected pack.' : state.message);
      setText('#ffh-dispatch-note', state.preorder
        ? 'Dispatch in approximately 2 weeks. Delivery is generally another 1–2 business days after dispatch.'
        : state.canAdd ? 'Dispatch within 1–2 business days. Delivery is generally another 1–2 business days after dispatch.' : cart ? 'Select an available pack and quantity to confirm dispatch timing.' : 'Dispatch timing will be confirmed after availability is checked.');
      root.classList.toggle('ffh-is-preorder', state.preorder);
      if (quantity) {
        quantity.disabled = busy;
        if (variant?.inventoryPolicy !== 'continue' && Number.isFinite(state.remaining) && state.remaining > 0) {
          quantity.max = String(state.remaining);
        } else quantity.removeAttribute('max');
      }
      if (submit) {
        submit.disabled = busy || cartPending || productPending || productNeedsRetry || unknownAddOutcome || !state.canAdd || !planValid || !Number.isSafeInteger(price);
        submit.setAttribute('aria-busy', String(busy));
        const label = busy ? 'Checking / adding…' : unknownAddOutcome ? 'Check your cart' : productNeedsRetry ? 'Availability unavailable' : !cart ? (cartPending ? 'Checking availability…' : 'Availability unavailable') : state.preorder ? 'Pre-order — add to cart' : variant?.available ? 'Add to cart' : 'Sold out';
        const labelNode = submit.querySelector('[data-ffh-submit-label]');
        if (labelNode) labelNode.textContent = label;
        else submit.textContent = label;
      }
    }

    async function request(url, options = {}, bodyType) {
      const controller = new AbortController();
      requests.add(controller);
      const timer = window.setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        if (bodyType) return { response, body: await response[bodyType]() };
        return response;
      } finally {
        window.clearTimeout(timer);
        requests.delete(controller);
      }
    }

    function productIdentity(value) {
      if (typeof value !== 'string' || !value) throw new Error('Product URL is missing.');
      const url = new URL(value, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid product origin.');
      const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
      if (!match) throw new Error('Invalid product URL.');
      return match[1];
    }

    function validateProduct(fresh) {
      const validID = value => (typeof value === 'number' && Number.isSafeInteger(value) && value > 0)
        || (typeof value === 'string' && /^[1-9]\d*$/.test(value));
      if (!fresh || productIdentity(fresh.productUrl) !== productIdentity(data.productUrl)
        || fresh.currency !== data.currency || !Array.isArray(fresh.variants)
        || fresh.variants.map(variant => `${variant.id}:${variant.pack}`).sort().join('|') !== originalVariantPacks) {
        throw new Error('Product identity or currency changed.');
      }
      const seenVariants = new Set();
      for (const variant of fresh.variants) {
        if (!validID(variant.id) || seenVariants.has(String(variant.id))
          || typeof variant.available !== 'boolean' || typeof variant.requiresSellingPlan !== 'boolean'
          || !Number.isSafeInteger(variant.price) || variant.price < 0
          || !Number.isSafeInteger(variant.inventoryQuantity)
          || !(variant.inventoryManagement == null || typeof variant.inventoryManagement === 'string')
          || !['continue', 'deny'].includes(variant.inventoryPolicy)
          || !Array.isArray(variant.sellingPlanAllocations)) throw new Error('Invalid variant data.');
        seenVariants.add(String(variant.id));
        const seenPlans = new Set();
        for (const plan of variant.sellingPlanAllocations) {
          if (!validID(plan.id) || seenPlans.has(String(plan.id)) || typeof plan.name !== 'string'
            || typeof plan.monthly !== 'boolean' || !Number.isSafeInteger(plan.price) || plan.price < 0) {
            throw new Error('Invalid selling plan data.');
          }
          seenPlans.add(String(plan.id));
        }
      }
    }

    async function refreshProduct() {
      productPending = true;
      syncSelection();
      try {
        productIdentity(data.productUrl);
        const url = new URL(data.productUrl, window.location.origin);
        const localeRoot = window.Shopify?.routes?.root || '/';
        if (url.pathname.startsWith('/products/') && localeRoot !== '/') {
          url.pathname = localeRoot.replace(/\/?$/, '/') + url.pathname.slice(1);
        }
        url.searchParams.set('section_id', 'ffh-storefront');
        const previewTheme = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
        const result = await request(url.pathname + url.search, { headers: { Accept: 'text/html' } }, 'text');
        if (!result.response.ok) throw new Error('Product information could not be loaded.');
        const page = new DOMParser().parseFromString(result.body, 'text/html');
        const source = page.querySelector('.ffh-storefront [data-ffh-product]');
        if (!source) throw new Error('Product section data is missing.');
        const fresh = JSON.parse(source.textContent);
        validateProduct(fresh);
        variants = fresh.variants;
        data = { ...data, variants };
        productNeedsRetry = false;
        return fresh;
      } catch (failure) {
        productNeedsRetry = true;
        throw failure;
      } finally {
        productPending = false;
        if (!lifecycle.signal.aborted) syncSelection();
      }
    }

    async function refreshCommerce() {
      // Wait for both reads even when one fails, keeping controls locked until
      // every outstanding check has finished. Neither request changes the cart.
      const results = await Promise.allSettled([refreshCart(), refreshProduct()]);
      const failed = results.find(result => result.status === 'rejected');
      if (failed) throw failed.reason;
    }

    async function refreshCart() {
      cartPending = true;
      syncSelection();
      try {
        const response = await request(cartJSONURL, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Cart could not be loaded.');
        const result = await response.json();
        if (!Array.isArray(result.items)) throw new Error('Invalid cart response.');
        cart = result;
        const itemCount = Number.isSafeInteger(result.item_count) ? result.item_count : result.items.reduce((count, item) => count + (Number(item.quantity) || 0), 0);
        for (const counter of $$('[data-ffh-cart-count]')) counter.textContent = String(itemCount);
        return result;
      } catch (failure) {
        cart = null;
        throw failure;
      } finally {
        cartPending = false;
        if (!lifecycle.signal.aborted) syncSelection();
      }
    }

    let retry = $('#ffh-retry-cart');
    if (!retry && form) {
      retry = document.createElement('button');
      retry.type = 'button';
      retry.id = 'ffh-retry-cart';
      retry.className = 'ffh-retry-cart';
      retry.textContent = 'Retry availability check';
      retry.hidden = true;
      ($('#ffh-cart-error') || form).insertAdjacentElement('afterend', retry);
    }
    const cartFailed = () => {
      error(productNeedsRetry
        ? 'We could not verify the latest stock, prices and purchase options. Retry the availability check or reload this page before adding a pack.'
        : 'We could not check your cart. Check your connection and retry, or open your cart.');
      if (retry) retry.hidden = false;
    };
    on(retry, 'click', async () => {
      if (busy || cartPending || productPending || unknownAddOutcome) return;
      busy = true;
      retry.hidden = true;
      error('');
      try {
        await refreshCommerce();
        status('Availability and prices refreshed. Review your selection before adding it to the cart.');
      } catch (_) { if (!lifecycle.signal.aborted) cartFailed(); }
      finally { busy = false; if (!lifecycle.signal.aborted) syncSelection(); }
    });

    function currentPhoto() {
      return data.gallery?.[galleryView === 'pack' ? selectedVariant()?.pack : 'single'];
    }
    function renderGallery() {
      const photo = currentPhoto();
      const packPhoto = data.gallery?.[selectedVariant()?.pack];
      if (!photo) return;
      const image = $('#gallery-image');
      if (image && photo.src) {
        image.removeAttribute('srcset');
        image.src = photo.src;
        image.alt = photo.alt || photo.title || '';
      }
      setText('#gallery-caption', photo.title || 'Product photo');
      $('#gallery-enlarge')?.setAttribute('aria-label', `Enlarge photo: ${photo.title || 'Product photo'}`);
      const thumbnail = $('#gallery-pack-thumb');
      if (thumbnail && (packPhoto?.thumbnail || packPhoto?.src)) thumbnail.src = packPhoto.thumbnail || packPhoto.src;
      for (const button of $$('[data-gallery-view]')) {
        button.setAttribute('aria-pressed', String(button.dataset.galleryView === galleryView));
        if (button.dataset.galleryView === 'pack') button.setAttribute('aria-label', `Show ${packPhoto?.title || 'selected pack'} photo`);
      }
    }

    const triggers = new WeakMap();
    let enquiryBackdrop;
    let activeEnquiry;
    function openDialog(dialog, trigger) {
      if (!dialog) return false;
      const enquiry = dialog.classList.contains('enquiry-dialog');
      if (typeof dialog[enquiry ? 'show' : 'showModal'] !== 'function') return false;
      triggers.set(dialog, trigger || document.activeElement);
      if (enquiry) {
        // A native modal's top layer would hide Shopify's body-level CAPTCHA.
        // This ordinary dialog/backdrop leaves that external UI interactive.
        if (activeEnquiry && activeEnquiry !== dialog && activeEnquiry.open) activeEnquiry.close();
        if (!enquiryBackdrop) {
          enquiryBackdrop = document.createElement('div');
          enquiryBackdrop.className = 'ffh-enquiry-backdrop';
          enquiryBackdrop.setAttribute('aria-hidden', 'true');
          root.append(enquiryBackdrop);
          on(enquiryBackdrop, 'click', () => activeEnquiry?.close());
        }
        activeEnquiry = dialog;
        enquiryBackdrop.hidden = false;
        dialog.setAttribute('aria-modal', 'false');
        if (!dialog.open) dialog.show();
      } else if (!dialog.open) dialog.showModal();
      dialog.scrollTop = 0;
      return true;
    }
    function showPhoto(title, src, alt, trigger, fullSizeLink = false) {
      const dialog = $('#ffh-photo-dialog');
      const content = dialog?.querySelector('#dialog-content');
      if (!content || !src) return false;
      content.replaceChildren();
      const image = document.createElement('img');
      image.className = 'dialog-photo';
      image.src = src;
      image.alt = alt || title;
      content.append(image);
      if (fullSizeLink) {
        const paragraph = document.createElement('p');
        const link = document.createElement('a');
        link.href = src;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = 'Open full-size label';
        paragraph.append(link);
        content.append(paragraph);
      }
      setText('#dialog-title', title);
      return openDialog(dialog, trigger);
    }

    on(root, 'click', event => {
      const opener = event.target.closest('[data-ffh-open-dialog], [data-contact], #stockist-enquiry');
      if (opener && root.contains(opener)) {
        const id = (opener.dataset.ffhOpenDialog || (opener.id === 'stockist-enquiry' ? 'ffh-stockist-dialog' : 'ffh-contact-dialog')).replace(/^#/, '');
        const dialog = $$('dialog').find(item => item.id === id);
        if (openDialog(dialog, opener)) event.preventDefault();
        return;
      }
      const label = event.target.closest('[data-label]');
      if (label && root.contains(label) && showPhoto('The approved bottle label.', label.href, 'Complete approved Fusion Feast House bottle label', label, true)) event.preventDefault();
    });
    on($('#gallery-enlarge'), 'click', event => {
      const photo = currentPhoto();
      if (photo) showPhoto(photo.title, photo.src, photo.alt, event.currentTarget);
    });
    for (const button of $$('[data-gallery-view]')) on(button, 'click', () => {
      galleryView = button.dataset.galleryView === 'pack' ? 'pack' : 'single';
      renderGallery();
    });

    const dialogs = $$('dialog');
    const returnedDialog = dialogs.find(dialog => `#${dialog.id}` === window.location.hash
      || [...dialog.querySelectorAll('form[id]')].some(form => `#${form.id}` === window.location.hash));
    for (const dialog of dialogs) {
      // Shopify can expose one contact POST result to both forms. Only the
      // form identified by the native return hash may display its success.
      if (dialog !== returnedDialog) {
        for (const result of dialog.querySelectorAll('[data-ffh-form-result="success"]')) result.hidden = true;
      }
      on(dialog, 'click', event => {
        if (event.target.closest('.dialog-close, .dialog-done, [data-ffh-close-dialog]')) dialog.close();
        else if (event.target === dialog) {
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
        }
      });
      on(dialog, 'close', () => {
        if (activeEnquiry === dialog) {
          activeEnquiry = null;
          if (enquiryBackdrop) enquiryBackdrop.hidden = true;
        }
        if (dialog.id === 'ffh-photo-dialog') dialog.querySelector('#dialog-content')?.replaceChildren();
        const trigger = triggers.get(dialog);
        if (!activeEnquiry && trigger?.isConnected) trigger.focus();
      });
      on(dialog, 'keydown', event => {
        if (dialog.classList.contains('enquiry-dialog')) {
          // Nonmodal enquiry dialogs must allow focus to reach external CAPTCHA
          // UI. Native validation/submission owns its focus and lifecycle.
          if (event.key === 'Escape' && !event.defaultPrevented) {
            event.preventDefault();
            dialog.close();
          }
          return;
        }
        if (event.key !== 'Tab') return;
        const controls = [...dialog.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
          .filter(element => !element.disabled && element.getClientRects().length);
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); return; }
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      });
      // Keep native contact POSTs, hCaptcha and Shopify success/errors intact.
      for (const field of dialog.querySelectorAll('input[type="text"][required], textarea[required]')) {
        on(field, 'input', () => field.setCustomValidity(field.value.trim() ? '' : 'Please enter a value.'));
        on(field, 'change', () => field.setCustomValidity(field.value.trim() ? '' : 'Please enter a value.'));
      }
      const result = dialog.querySelector('[data-ffh-form-result]');
      if (dialog === returnedDialog && result && ['success', 'error'].includes(result.dataset.ffhFormResult)) {
        openDialog(dialog);
        result.setAttribute('tabindex', '-1');
        result.focus();
      }
    }

    const menuButton = $('#menu-toggle');
    const header = $('.header');
    const mobileLayout = window.matchMedia('(max-width: 700px)');
    function closeMenu(returnFocus = false) {
      header?.classList.remove('menu-open');
      menuButton?.setAttribute('aria-expanded', 'false');
      if (returnFocus) menuButton?.focus();
    }
    on(menuButton, 'click', () => {
      const open = header?.classList.toggle('menu-open');
      menuButton.setAttribute('aria-expanded', String(Boolean(open)));
    });
    on(header, 'keydown', event => {
      if (event.key === 'Escape' && header.classList.contains('menu-open')) { event.preventDefault(); closeMenu(true); }
    });
    for (const link of header?.querySelectorAll('a[href^="#"]') || []) on(link, 'click', () => {
      if (!header.classList.contains('menu-open')) return;
      closeMenu();
      let target;
      try { target = document.getElementById(decodeURIComponent(link.hash.slice(1))); } catch (_) { return; }
      if (target) { target.tabIndex = -1; target.focus({ preventScroll: true }); }
    });
    on(document, 'click', event => { if (header && !header.contains(event.target)) closeMenu(); });
    on(mobileLayout, 'change', () => closeMenu());

    if (form && submit && quantity && variantInput && variants.length) {
      const nativeVariant = $('#ffh-native-variant');
      variantInput.disabled = false;
      if (nativeVariant) nativeVariant.disabled = true;
      root.classList.add('ffh-js');
      const once = $('[name="purchase"][value="once"]');
      if (once) once.checked = true;
      // Honour only a monthly plan actually offered for the selected variant.
      // An absent, stale or unrelated URL parameter keeps the one-time default.
      const requestedPlanID = new URL(window.location.href).searchParams.get('selling_plan');
      const requestedPlan = monthlyPlan(selectedVariant(), requestedPlanID);
      const monthly = $('[name="purchase"][value="monthly"]');
      if (monthly && requestedPlan && requestedPlanID && String(requestedPlan.id) === requestedPlanID) {
        preferredMonthlyPlanID = requestedPlanID;
        monthly.checked = true;
      }
      for (const radio of $$('[name="purchase"], [name="pack"]')) on(radio, 'change', () => {
        if (busy) return;
        error('');
        syncSelection();
        if (radio.name === 'pack') { galleryView = 'pack'; renderGallery(); }
      });
      on(quantity, 'input', () => { if (!busy) { error(''); syncSelection(); } });
      on(form, 'submit', async event => {
        event.preventDefault();
        if (busy || cartPending || productPending || productNeedsRetry || unknownAddOutcome) return;
        if (!form.reportValidity()) return;
        const previousVariant = selectedVariant();
        const previousMode = selectedMode();
        const previousPlan = previousMode === 'monthly' ? monthlyPlan(previousVariant) : null;
        const previous = {
          preorder: availability(previousVariant, selectedQuantity()).preorder,
          mode: previousMode,
          price: previousPlan ? previousPlan.price : previousVariant?.price,
          planID: previousPlan?.id ?? null,
          planName: previousPlan?.name ?? null
        };
        busy = true;
        error('');
        status('Checking current stock, prices and your cart…');
        if (retry) retry.hidden = true;
        let adding = false;
        let added = false;
        try {
          await refreshCommerce();
          if (lifecycle.signal.aborted) return;
          const variant = selectedVariant();
          const count = selectedQuantity();
          const plan = selectedMode() === 'monthly' ? monthlyPlan(variant) : null;
          const state = availability(variant, count);
          if (!state.canAdd || (selectedMode() === 'monthly' && !plan) || (variant.requiresSellingPlan && !plan)) {
            error(state.canAdd ? 'Choose an available purchase option.' : state.message);
            status('');
            return;
          }
          const price = plan ? plan.price : variant.price;
          const purchaseChanged = previous.mode !== selectedMode() || previous.price !== price
            || String(previous.planID) !== String(plan?.id ?? null) || previous.planName !== (plan?.name ?? null);
          if (purchaseChanged || state.preorder !== previous.preorder) {
            status(purchaseChanged
              ? 'The current price or purchase option has changed. Review the updated selection and dispatch terms, then add it to your cart.'
              : state.preorder
                ? 'Stock or the quantity in your cart has changed. Please review the pre-order dispatch terms, then choose Pre-order — add to cart.'
                : 'Availability has changed and this quantity no longer needs a pre-order. Review the updated dispatch information, then add it to your cart.');
            return;
          }
          const item = { id: variant.id, quantity: count };
          if (plan) item.selling_plan = plan.id;
          // Carry the same properties as the native form, including stable
          // dispatch terms. Do not rewrite existing cart lines or their keys.
          const properties = Object.create(null);
          for (const [name, value] of new FormData(form)) {
            const property = /^properties\[(.+)\]$/.exec(name);
            if (property && typeof value === 'string') properties[property[1]] = value;
          }
          if (Object.keys(properties).length) item.properties = properties;
          status(state.preorder ? 'Adding your pre-order to the cart…' : 'Adding to your cart…');
          adding = true;
          const response = await request(addURL, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [item] })
          });
          const result = await response.json();
          if (!response.ok) {
            adding = false;
            error(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'This item could not be added. Please check your cart and try again.');
            // A rejected quantity can still change the cart, and availability
            // may have changed between verification and Shopify's final check.
            await refreshCommerce();
            status('Your cart, stock and prices have been checked. Review the quantity before trying again.');
            return;
          }
          added = true;
          status('Added to your cart. Opening your cart…');
          window.location.assign(cartURL);
        } catch (_) {
          if (lifecycle.signal.aborted) return;
          status('');
          if (adding) {
            unknownAddOutcome = true;
            error('We could not confirm whether this item was added. Open your cart to check before placing it again.');
          } else cartFailed();
        } finally {
          busy = added && !unknownAddOutcome;
          if (!lifecycle.signal.aborted) syncSelection();
        }
      });
      refreshCart().catch(() => { if (!lifecycle.signal.aborted) cartFailed(); });
    }
    renderGallery();
    return { destroy() {
      lifecycle.abort();
      for (const controller of requests) controller.abort();
      if (enquiryBackdrop) enquiryBackdrop.hidden = true;
      for (const dialog of $$('dialog[open]')) dialog.close();
    } };
  }

  function rootsWithin(scope) {
    return [...(scope.matches?.('.ffh-storefront') ? [scope] : []), ...scope.querySelectorAll('.ffh-storefront')];
  }
  function mount(scope) {
    for (const root of rootsWithin(scope)) {
      if (!instances.has(root)) instances.set(root, createStorefront(root));
    }
  }
  window.FFHStorefront = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document), { once: true });
  else mount(document);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const root of rootsWithin(event.target)) { instances.get(root)?.destroy(); instances.delete(root); }
  });
})();
